import { FC, useMemo, useEffect, useCallback, useRef } from 'react'
import styles from 'components/features/future/FuturePage/FuturePage.styl'
import Button from 'components/features/pfm/Button/Button'
import Anchor from 'components/atoms/navigation/Anchor'
import ModuleWrapper2020 from 'components/features/pfm/ModuleWrapper2020/ModuleWrapper2020'
import HighlightsContainer from 'components/features/pfm/Highlights/HighlightsContainer'
import { useStoreDispatch, useShallowSelector, useStoreSelector } from 'helpers/reactReduxHelpers'
import { getFutureActionCreator } from 'components/features/future/actions'
import { isMemberPremiumSelector } from 'selectors/globalSelectors'
import { LoadingOverlay } from 'components/atoms/preloaders/LoadingOverlay'
import { FutureNetworthForecastChart } from 'components/features/future/FutureNetworthForecastChart/FutureNetworthForecastChart'
import { FutureLayersBar } from 'components/features/future/FutureLayersBar/FutureLayersBar'
import { FutureNetworthBreakdownModule } from 'components/features/future/FutureNetworthBreakdownModule/FutureNetworthBreakdownModule'
import { modifyFloatingPaneActionCreator, openSubscriptionPageActionCreator } from 'actions/floatingPanesActions'
import InternalLayoutContainer from 'components/features/pfm/InternalLayout/InternalLayoutContainer'
import InternalSidebarContainer from 'components/features/pfm/InternalSidebar/InternalSidebarContainer'
import { SingleColumnLayout } from 'components/layouts/Layouts'
import { NotificationTrayWrapper } from 'components/features/pfm/NotificationTray/NotificationTrayWrapper'
import { InternalPageRedirect } from 'components/features/pfm/InternalPageRedirect/InternalPageRedirect'
import { DesktopInternalStickyHeader } from 'components/features/pfm/DesktopInternalStickyHeader/DesktopInternalStickyHeader'
import { PageParamsStickyHeader } from 'components/features/pfm/PageParamsHeader/PageParamsStickyHeader'
import { useShouldShowPageParamsStickyHeader } from 'helpers/pageHelpers'
import { PageParamsHeader } from 'components/features/pfm/PageParamsHeader/PageParamsHeader'
import { firstLayersUIDsSelector, layersSelector } from 'components/features/future/selectors'
import { FutureLayerPreview } from 'components/features/future/FutureLayerPreview/FutureLayerPreview'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'

type FuturePageProps = {

}

export const FuturePage: FC<FuturePageProps> = (props) => {

  return (
    <InternalPageRedirect>
      <FuturePageInner { ...props } />
    </InternalPageRedirect>
  )

}
const FuturePageInner: FC<FuturePageProps> = (props) => {
  const {} = props

  const dispatch = useStoreDispatch()
  useEffect(() => {
    dispatch(getFutureActionCreator.request({}))
  }, [dispatch])

  const isMemberPremium = useStoreSelector(isMemberPremiumSelector)
  const layersCount = useStoreSelector((state) => layersSelector(state).length)
  const addLayerIsLocked = !isMemberPremium && layersCount >= 2

  const handleAddLayer = useCallback(() => {
    if (addLayerIsLocked) {
      dispatch(openSubscriptionPageActionCreator())
      return
    }

    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'all-layers',
    }))
  }, [addLayerIsLocked, dispatch])

  const handleAdjustAssumptions = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'assumptions',
    }))
  }, [dispatch])

  const handleSeeAllLayers = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator('future', {
      mode: 'member-layers',
    }))
  }, [dispatch])

  const { isLoading } = useShallowSelector((state) => {
    const { request } = state.data.future
    return {
      isLoading: request.isLoading,
    }
  })

  const seeAllChild = useMemo(() => (
    <div className={ styles.seeAllButton }>
      <Button
        style='textOnly'
        size='tiny'
        text='See All'
        withChevron
        onClick={ handleSeeAllLayers }
      />
    </div>
  ), [handleSeeAllLayers])

  const adjustChild = useMemo(() => (
    <div className={ styles.adjustButton }>
      <Button
        style='textOnly'
        size='tiny'
        text='Adjust'
        iconSlug='gear'
        onClick={ handleAdjustAssumptions }
      />
    </div>
  ), [handleAdjustAssumptions])

  const addLayerButtonChild = useMemo(() => (
    <div className={ styles.addLayerButton }>
      <Button
        theme={ addLayerIsLocked ? 'golden' : 'blue' }
        size='small'
        iconSlug={ addLayerIsLocked ? 'lock' : 'plus' }
        fullWidth
        text='Add Goal or Event'
        onClick={ handleAddLayer }
      />
    </div>
  ), [addLayerIsLocked, handleAddLayer])

  const layersUIDs = useStoreSelector(firstLayersUIDsSelector)
  const layersChild = useMemo(() => {
    if (!layersUIDs.length) {
      return null
    }

    const memberLayers = layersUIDs.map((uid) => (
      <div key={ uid } className={ styles.layerPreviewContainer }>
        <FutureLayerPreview uid={ uid } />
      </div>
    ))

    return (
      <section className={ styles.module }>
        <ModuleWrapper2020
          title='Upcoming Plans'
          card={ false }
          isFlexLayout
          action={ seeAllChild }
        >
          { memberLayers }
        </ModuleWrapper2020>
      </section>
    )
  }, [layersUIDs, seeAllChild])

  const forecastChild = useMemo(() => (
    <section className={ styles.module }>
      <ModuleWrapper2020
        title='Net Worth Forecast'
        isFlexLayout
        anchor='future_trend'
        action={ adjustChild }
        noPadding
        forceRoundedCorners
        corners='all'
        shadowTheme='medium'
      >
        <CardBlock
          shadowTheme='no-shadow'
          noBorders
          withPadding
          forceRoundedCorners
          corners='all'
        >
          <FutureNetworthForecastChart />
          <FutureLayersBar />
        </CardBlock>
      </ModuleWrapper2020>
    </section>
  ), [adjustChild])

  const breakdownChild = useMemo(() => (
    <section className={ styles.module }>
      <ModuleWrapper2020
        title='Assets &amp; Debts Forecast'
        anchor='future_breakdown'
        isFlexLayout
        action={ adjustChild }
        noPadding
        forceRoundedCorners
        corners='all'
        shadowTheme='medium'
      >
        <CardBlock
          shadowTheme='no-shadow'
          noBorders
          withPadding
          forceRoundedCorners
          corners='all'
        >
          <FutureNetworthBreakdownModule />
        </CardBlock>
      </ModuleWrapper2020>
    </section>
  ), [adjustChild])

  const preloaderChild = useMemo(() => {
    if (!isLoading) {
      return null
    }

    return (
      <LoadingOverlay />
    )
  }, [isLoading])

  const staticHeaderRef = useRef<HTMLDivElement>(null)
  const shouldShowStickyHeader = useShouldShowPageParamsStickyHeader(staticHeaderRef)

  const notifications = useMemo(() => (
    <NotificationTrayWrapper
      renderLevel='page'
      placement='future_general'
      isPositionRelative
    />
  ), [])

  return (
    <InternalLayoutContainer
      sidebar={ <InternalSidebarContainer /> }
      notifications={ notifications }
      stickyHeader={ <PageParamsStickyHeader type='future' /> }
      shouldShowStickyHeader={ shouldShowStickyHeader }
    >

      <Anchor placement='future'>
        <Anchor placement='future_general'>
          <DesktopInternalStickyHeader isShown={ shouldShowStickyHeader }>
            <PageParamsStickyHeader type='future' />
          </DesktopInternalStickyHeader>
          <SingleColumnLayout
            header={ <PageParamsHeader type='future' nodeRef={ staticHeaderRef } /> }
          >
            { layersChild }
            { addLayerButtonChild }
            { forecastChild }
            { breakdownChild }
            { preloaderChild }
          </SingleColumnLayout>
          <section className={ styles.quickLookSection }>
            <HighlightsContainer placement='future_opportunities' />
          </section>
        </Anchor>
      </Anchor>
    </InternalLayoutContainer>
  )
}
